
import { Component, Vue } from 'vue-property-decorator';
import { SchemeModel } from '@/models/schemeModel';
import { extractVideoIds, schemeNodeModelToTreeNode } from '@/services/scheme';
import { SchemeNodeModel } from '@/models/schemeNodeModel';
import { videosDb } from '@/services/db/videos';
import _difference from 'lodash/difference';
import asyncPool from 'tiny-async-pool';
import Dexie from 'dexie';

@Component({
  name: 'SchemeTreeView',
})
export default class SchemeTreeView extends Vue {
  private saving: 'no' | 'videos' | 'nodes' | 'theme' = 'no';
  private warning = '';

  get downloadVideos() {
    return this.$store.state.nav.downloadVideos;
  }

  set downloadVideos(value: boolean) {
    this.$store.commit('nav/updateSchemeDownloadVideos', value);
  }

  get selected() {
    return this.$store.getters['nav/schemeSelectedSkills'];
  }

  /**
   * Set automatically by treeview when check boxes
   */
  set selected(value: string[]) {
    this.$store.commit('nav/updateSchemeSelectedSkills', value);
  }

  get items() {
    // must wrap the objects in an array to get past the treeview check
    const schemes = this.$store.getters['schemes/schemes'];
    return schemes
      .map((scheme: SchemeModel) => {
        const schemeNodes = this.$store.getters['schemes/getScheme'](scheme.slug);
        return schemeNodeModelToTreeNode(schemeNodes, false, [0, 1], scheme.slug, schemes.length > 1);
      })
      .flat();
  }

  get buttonText() {
    if (this.saving === 'nodes') {
      return this.$t('offline.saving_skills');
    }
    if (this.saving === 'videos') {
      return this.$t('offline.saving_videos');
    }
    if (this.saving === 'theme') {
      return this.$t('offline.saving_theme');
    }
    return this.$t('offline.save');
  }

  get downloadProgress() {
    return this.$store.getters['schemes/downloadPercentage'];
  }

  async getVideoIds(schemeNodeIds: Array<string>): Promise<[string[], string[], Record<string, string[]>]> {
    if (!this.downloadVideos) {
      return [[], [], {}];
    }

    const videoToNodeMap = schemeNodeIds.reduce((map: Record<string, string[]>, schemeNodeId: string) => {
      const [slug, nodeId] = schemeNodeId.split(':');
      const node = this.$store.getters['schemes/getNode'](slug, nodeId) as SchemeNodeModel;
      const video = node.content.find(content => content.type === 'video');
      (video ? extractVideoIds(video.value as string) : []).forEach(videoId => {
        if (videoId in map) {
          map[videoId].push(node.title);
        } else {
          map[videoId] = [node.title];
        }
      });
      return map;
    }, {});

    const newVideoIds = Object.keys(videoToNodeMap);

    const existingVideoIds = await videosDb.getVideoIds();

    // Delete any videos we don't need anymore
    const toDelete = _difference(existingVideoIds, newVideoIds);

    // Find any videos we don't already have
    const toFetch = _difference(newVideoIds, existingVideoIds);

    return [toDelete, toFetch, videoToNodeMap];
  }

  updateProgress(current: number, total: number) {
    const percentage = Math.round(((current + 1) / total) * 100);
    this.$store.commit('schemes/updateDownloadPercentage', percentage);
    return current + 1;
  }

  async saveOffline(force: boolean) {
    this.saving = 'nodes';

    if (force) {
      this.warning = '';
    }
    const [toDelete, toFetch, videoToNodeMap] = await this.getVideoIds(this.selected);

    if (!force && toFetch.length > 0 && 'storage' in navigator) {
      // navigator.storage is only set when running on https
      const { quota, usage } = await navigator.storage.estimate();
      if (quota && usage) {
        const remainingMB = (quota - usage) / (1024 * 1024);

        // assume average 30MB video size
        if (toFetch.length * 30 > remainingMB) {
          this.warning = this.$t('offline.quota_warning', {
            remaining: Math.round(remainingMB),
            videos: toFetch.length,
          });
          this.saving = 'no';
          return;
        }
      }
    }

    this.warning = '';
    let synced = this.updateProgress(-1, 1); // reset

    // purge any unneeded videos
    if (toDelete.length) {
      videosDb.clearVideos(toDelete);
    }

    try {
      // first cache any node images by fetching to cache
      const cacheNode = async (id: string) => {
        const [slug, schemeNodeId] = id.split(':');
        await this.$store.dispatch('schemes/saveSchemeNodeInCache', { slug, schemeNodeId });
        if (this.downloadVideos) {
          // we also need to pre-fetch and cache the video details (name, online url -- the content is cached below)
          // since this is fetched in RenderSchemeVideo
          const node = this.$store.getters['schemes/getNode'](slug, schemeNodeId) as SchemeNodeModel;
          try {
            await this.$store.dispatch('schemes/addVideosToNode', { node });
          } catch (error) {
            console.log(`Failed to pre-load videos for ${node.id}`, error);
          }
        }
      };

      const videoErrors: Array<string> = [];

      const cacheVideo = async (videoId: string) => {
        let r = false;
        try {
          r = await this.$store.dispatch('schemes/downloadSchemeVideo', videoId);
        } catch (error) {
          console.log(`Failed to download ${videoId}`, error);
        }
        if (!r) {
          videoErrors.push(videoId);
        }
      };

      // fetch up to 5 nodes in parallel
      for await (const _r of asyncPool(5, this.selected, cacheNode)) {
        synced = this.updateProgress(synced, this.selected.length);
      }

      if (this.downloadVideos) {
        synced = this.updateProgress(-1, 1); // reset
        this.saving = 'videos';

        // download videos
        for await (const _r of asyncPool(2, toFetch, cacheVideo)) {
          synced = this.updateProgress(synced, toFetch.length);
        }
      }

      const client = this.$store.getters['auth/clientDetails'];
      const themeName = client ? client.theme_name : 'default';
      const themeAssets = (process.env.THEME_IMAGES as unknown) as Array<Array<string>>;
      const ignoreFetch = async (url: string) => {
        try {
          await fetch(url);
        } catch {
          // NOTE: Ignoring this for now, seems to fail intermittently for some people only, for some files only.
          // this.$alert('warning', this.$t('offline.error_saving_theme'));
        }
      };
      if (process.env.THEME_IMAGES && Object.prototype.hasOwnProperty.call(themeAssets, themeName)) {
        synced = this.updateProgress(-1, 1); // reset
        this.saving = 'theme';
        const themeBaseUrl = client.theme_url.replace('theme.css', 'images');
        const toFetch = themeAssets[themeName].map(url => `${themeBaseUrl}/${url}`);

        // Ignore errors fetching these
        for await (const _r of asyncPool(5, toFetch, ignoreFetch)) {
          synced = this.updateProgress(synced, themeAssets.length);
        }
      }

      this.$alert('success', this.$t('offline.saved_msg'));
      if (videoErrors.length) {
        const failedNodes = videoErrors.flatMap(videoId => (videoId in videoToNodeMap ? videoToNodeMap[videoId] : []));
        this.$alert('warning', this.$t('offline.error_saving_videos', { skills: failedNodes.join(', ') }));
      }
    } catch (error) {
      console.error('[Error saving offline skills]:', error);
      if (error instanceof Dexie.DexieError) {
        this.$alert('error', this.$t('offline.out_of_space'));
      } else {
        this.$alert('error', this.$t('offline.error_saving'));
      }
    }

    this.saving = 'no';
  }
}
